"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { ArrowRight, Check, Loader2 } from "lucide-react"

interface FormData {
  name: string
  email: string
  assetType: string
  message: string
}

export default function SignupForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [assetType, setAssetType] = useState("other")

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // In a real app, you would send this data to your API
    console.log("Form data:", { ...data, assetType })

    setIsSubmitting(false)
    setIsSubmitted(true)
    reset()

    // Reset success message after 5 seconds
    setTimeout(() => {
      setIsSubmitted(false)
    }, 5000)
  }

  const inputClasses = (hasError: boolean) =>
    `w-full rounded-xl border ${
      hasError ? "border-red-500/50" : "border-white/10"
    } bg-white/5 px-4 py-3 text-white placeholder-gray-500 transition-all focus:border-teal-500/50 focus:bg-white/[0.07] focus:outline-none focus:ring-1 focus:ring-teal-500/30`

  return (
    <div>
      {isSubmitted ? (
        <div className="rounded-2xl border border-teal-500/20 bg-teal-500/10 p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 shadow-lg shadow-teal-500/20">
            <Check className="h-7 w-7 text-white" />
          </div>
          <h3 className="mb-2 text-xl font-bold text-white">Thank You!</h3>
          <p className="text-gray-300">
            You've been added to our beta waitlist. We'll notify you when PartnerEZ is ready for you to try.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-300">
              Full Name
            </label>
            <input
              id="name"
              placeholder="John Doe"
              {...register("name", { required: "Name is required" })}
              className={inputClasses(!!errors.name)}
            />
            {errors.name && <p className="text-sm text-red-400">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              className={inputClasses(!!errors.email)}
            />
            {errors.email && <p className="text-sm text-red-400">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="assetType" className="block text-sm font-medium text-gray-300">
              What type of asset are you interested in sharing?
            </label>
            <select
              id="assetType"
              value={assetType}
              onChange={(e) => setAssetType(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white transition-all focus:border-teal-500/50 focus:bg-white/[0.07] focus:outline-none focus:ring-1 focus:ring-teal-500/30"
            >
              <option value="plane">Aircraft</option>
              <option value="boat">Boat</option>
              <option value="rv">RV</option>
              <option value="home">Vacation Home</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="message" className="block text-sm font-medium text-gray-300">
              Additional Information (Optional)
            </label>
            <textarea
              id="message"
              placeholder="Tell us more about your partnership needs..."
              {...register("message")}
              className="min-h-[100px] w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-gray-500 transition-all focus:border-teal-500/50 focus:bg-white/[0.07] focus:outline-none focus:ring-1 focus:ring-teal-500/30"
            />
          </div>

          <button
            type="submit"
            className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 px-4 py-3.5 font-medium text-white shadow-lg shadow-teal-500/20 transition-all hover:shadow-teal-500/30 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:ring-offset-2 focus:ring-offset-[#0a0f1a] disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Join Waitlist
                <ArrowRight className="ml-2 inline h-4 w-4" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
